// ============================================================================
// VALIDATION ENGINE - Core validation logic
// ============================================================================

import {
  DesignGraph,
  DesignNode,
  DesignEdge,
  ChallengeSpec,
  ValidationRule,
  RuleSet,
  Violation,
  ValidationResult,
  ValidationContext,
  ValidationIndices,
  ComponentCategory,
  ComponentDefinition,
  Protocol,
  UUID,
  DimensionScore,
} from './types';
import { DEFAULT_RULESET, getFastRules } from './rules';
import { getComponentById, COMPONENT_ONTOLOGY } from './ontology';

// Default challenge spec for when none is provided
const DEFAULT_CHALLENGE_SPEC: ChallengeSpec = {
  id: 'default',
  title: 'System Design Challenge',
  difficulty: 'intermediate',
  description: 'Design a scalable system',
  functionalRequirements: [],
  requirements: {
    qpsRead: 10000,
    qpsWrite: 1000,
    latencyReadP99Ms: 100,
    latencyWriteP99Ms: 200,
    readWriteRatio: 10,
    durability: 0.99999,
    consistency: 'eventual',
    monthlyBudget: 5000,
    regions: ['us-east-1'],
    multiRegionActiveActive: false,
    availabilitySla: 99.9,
    dataRetentionDays: 365,
    expectedDataSizeGb: 100,
    peakTrafficMultiplier: 2,
    realTimeRequired: false,
    authRequired: true,
    publicFacing: true,
  },
  scoringWeights: {
    correctness: 0.25,
    scalability: 0.15,
    reliability: 0.15,
    latency: 0.15,
    cost: 0.10,
    security: 0.10,
    operability: 0.10,
  },
  hints: [],
  expectedPatterns: {
    requiredSubtypes: [],
    requiredPatterns: [],
    antiPatterns: [],
  },
  timeLimitMinutes: 45,
  tags: [],
};

// ============================================================================
// INDEX BUILDING
// ============================================================================

function buildIndices(design: DesignGraph): ValidationIndices {
  const nodeById = new Map<UUID, DesignNode>();
  const nodesBySubtype = new Map<string, DesignNode[]>();
  const nodesByCategory = new Map<ComponentCategory, DesignNode[]>();
  const edgeById = new Map<UUID, DesignEdge>();
  const edgesBySource = new Map<UUID, DesignEdge[]>();
  const edgesByTarget = new Map<UUID, DesignEdge[]>();
  const edgesByProtocol = new Map<Protocol, DesignEdge[]>();
  const adjacencyMatrix = new Map<UUID, Set<UUID>>();

  // Index nodes
  for (const node of design.nodes) {
    nodeById.set(node.id, node);

    const subtype = node.component?.subtype;
    if (subtype) {
      if (!nodesBySubtype.has(subtype)) nodesBySubtype.set(subtype, []);
      nodesBySubtype.get(subtype)!.push(node);
    }

    const category = node.component?.category;
    if (category) {
      if (!nodesByCategory.has(category)) nodesByCategory.set(category, []);
      nodesByCategory.get(category)!.push(node);
    }

    adjacencyMatrix.set(node.id, new Set());
  }

  // Index edges
  for (const edge of design.edges) {
    edgeById.set(edge.id, edge);

    if (!edgesBySource.has(edge.sourceNodeId)) edgesBySource.set(edge.sourceNodeId, []);
    edgesBySource.get(edge.sourceNodeId)!.push(edge);

    if (!edgesByTarget.has(edge.targetNodeId)) edgesByTarget.set(edge.targetNodeId, []);
    edgesByTarget.get(edge.targetNodeId)!.push(edge);

    if (!edgesByProtocol.has(edge.protocol)) edgesByProtocol.set(edge.protocol, []);
    edgesByProtocol.get(edge.protocol)!.push(edge);

    adjacencyMatrix.get(edge.sourceNodeId)?.add(edge.targetNodeId);
  }

  return {
    nodeById,
    nodesBySubtype,
    nodesByCategory,
    edgeById,
    edgesBySource,
    edgesByTarget,
    edgesByProtocol,
    adjacencyMatrix,
  };
}

// ============================================================================
// CONDITION EVALUATORS
// ============================================================================

interface ConditionResult {
  matches: boolean;
  affectedNodes: UUID[];
  affectedEdges: UUID[];
}

type ConditionEvaluator = (
  params: Record<string, unknown>,
  ctx: ValidationContext
) => ConditionResult;

const conditionEvaluators: Record<string, ConditionEvaluator> = {
  node_exists: (params, ctx) => {
    const { category, subtype, minCount = 1, instanceCount } = params;
    let nodes: DesignNode[] = [];

    if (subtype) {
      const subtypeStr = subtype as string;
      if (subtypeStr.includes('*')) {
        const prefix = subtypeStr.replace('.*', '').replace('*', '');
        for (const [key, value] of ctx.indices.nodesBySubtype) {
          if (key.startsWith(prefix)) nodes.push(...value);
        }
      } else {
        nodes = ctx.indices.nodesBySubtype.get(subtypeStr) ?? [];
      }
    } else if (category) {
      nodes = ctx.indices.nodesByCategory.get(category as ComponentCategory) ?? [];
    }

    // Filter by instance count if specified
    if (instanceCount !== undefined) {
      nodes = nodes.filter(n => (n.config.instanceCount || 1) === instanceCount);
    }

    return {
      matches: nodes.length >= (minCount as number),
      affectedNodes: nodes.map(n => n.id),
      affectedEdges: [],
    };
  },

  node_missing: (params, ctx) => {
    const result = conditionEvaluators.node_exists(params, ctx);
    return {
      matches: !result.matches,
      affectedNodes: [],
      affectedEdges: [],
    };
  },

  edge_exists: (params, ctx) => {
    const {
      protocol,
      sourceCategory,
      targetCategory,
      targetCategoryNot,
      targetSubtypeNot,
      semanticNot,
    } = params;

    let edges = [...ctx.design.edges];

    if (protocol) {
      edges = edges.filter(e => e.protocol === protocol);
    }

    if (sourceCategory) {
      const categories = Array.isArray(sourceCategory) ? sourceCategory : [sourceCategory];
      edges = edges.filter(e => {
        const node = ctx.indices.nodeById.get(e.sourceNodeId);
        return node && categories.includes(node.component?.category);
      });
    }

    if (targetCategory) {
      const categories = Array.isArray(targetCategory) ? targetCategory : [targetCategory];
      edges = edges.filter(e => {
        const node = ctx.indices.nodeById.get(e.targetNodeId);
        return node && categories.includes(node.component?.category);
      });
    }

    if (targetCategoryNot) {
      const categories = Array.isArray(targetCategoryNot) ? targetCategoryNot : [targetCategoryNot];
      edges = edges.filter(e => {
        const node = ctx.indices.nodeById.get(e.targetNodeId);
        return node && !categories.includes(node.component?.category);
      });
    }

    if (targetSubtypeNot && Array.isArray(targetSubtypeNot)) {
      edges = edges.filter(e => {
        const node = ctx.indices.nodeById.get(e.targetNodeId);
        return node && !targetSubtypeNot.includes(node.component?.subtype);
      });
    }

    if (semanticNot && Array.isArray(semanticNot)) {
      edges = edges.filter(e => !semanticNot.includes(e.semantic));
    }

    return {
      matches: edges.length > 0,
      affectedNodes: [],
      affectedEdges: edges.map(e => e.id),
    };
  },

  edge_missing: (params, ctx) => {
    const result = conditionEvaluators.edge_exists(params, ctx);
    return {
      matches: !result.matches,
      affectedNodes: [],
      affectedEdges: [],
    };
  },

  path_exists: (params, ctx) => {
    const { fromSubtype, fromCategory, toSubtype, toCategory, maxHops = 10 } = params;

    let fromNodes: DesignNode[] = [];
    if (fromSubtype) {
      fromNodes = ctx.indices.nodesBySubtype.get(fromSubtype as string) ?? [];
    } else if (fromCategory) {
      fromNodes = ctx.indices.nodesByCategory.get(fromCategory as ComponentCategory) ?? [];
    }

    let toNodeIds: Set<UUID> = new Set();
    if (toSubtype) {
      const nodes = ctx.indices.nodesBySubtype.get(toSubtype as string) ?? [];
      nodes.forEach(n => toNodeIds.add(n.id));
    } else if (toCategory) {
      const nodes = ctx.indices.nodesByCategory.get(toCategory as ComponentCategory) ?? [];
      nodes.forEach(n => toNodeIds.add(n.id));
    }

    // BFS to find path
    for (const startNode of fromNodes) {
      const visited = new Set<UUID>();
      const queue: Array<{ nodeId: UUID; depth: number }> = [{ nodeId: startNode.id, depth: 0 }];

      while (queue.length > 0) {
        const { nodeId, depth } = queue.shift()!;

        if (toNodeIds.has(nodeId) && nodeId !== startNode.id) {
          return {
            matches: true,
            affectedNodes: [startNode.id, nodeId],
            affectedEdges: [],
          };
        }

        if (depth >= (maxHops as number) || visited.has(nodeId)) continue;
        visited.add(nodeId);

        const neighbors = ctx.indices.adjacencyMatrix.get(nodeId) ?? new Set();
        for (const neighbor of neighbors) {
          queue.push({ nodeId: neighbor, depth: depth + 1 });
        }
      }
    }

    return { matches: false, affectedNodes: [], affectedEdges: [] };
  },

  path_missing: (params, ctx) => {
    const result = conditionEvaluators.path_exists(params, ctx);
    return {
      matches: !result.matches,
      affectedNodes: [],
      affectedEdges: [],
    };
  },

  node_count: (params, ctx) => {
    const { category, subtype, operator, value } = params;
    let count = 0;

    if (subtype) {
      count = ctx.indices.nodesBySubtype.get(subtype as string)?.length ?? 0;
    } else if (category) {
      count = ctx.indices.nodesByCategory.get(category as ComponentCategory)?.length ?? 0;
    } else {
      count = ctx.design.nodes.length;
    }

    const ops: Record<string, (a: number, b: number) => boolean> = {
      '==': (a, b) => a === b,
      '!=': (a, b) => a !== b,
      '<': (a, b) => a < b,
      '<=': (a, b) => a <= b,
      '>': (a, b) => a > b,
      '>=': (a, b) => a >= b,
    };

    return {
      matches: ops[operator as string]?.(count, value as number) ?? false,
      affectedNodes: [],
      affectedEdges: [],
    };
  },

  graph_property: (params, ctx) => {
    const { property, operator, value } = params;

    const properties: Record<string, () => number> = {
      totalNodes: () => ctx.design.nodes.length,
      totalEdges: () => ctx.design.edges.length,
      maxSyncChainDepth: () => calculateMaxChainDepth(ctx),
      capacityRatio: () => calculateCapacityRatio(ctx),
    };

    const actual = properties[property as string]?.() ?? 0;

    const ops: Record<string, (a: number, b: number) => boolean> = {
      '==': (a, b) => a === b,
      '<': (a, b) => a < b,
      '>': (a, b) => a > b,
      '<=': (a, b) => a <= b,
      '>=': (a, b) => a >= b,
    };

    return {
      matches: ops[operator as string]?.(actual, value as number) ?? false,
      affectedNodes: [],
      affectedEdges: [],
    };
  },

  challenge_requirement: (params, ctx) => {
    const { field, operator = '==', value } = params;
    const actual = (ctx.challenge.requirements as any)[field as string];

    if (operator === '==') {
      return { matches: actual === value, affectedNodes: [], affectedEdges: [] };
    }
    if (operator === '>') {
      return { matches: actual > (value as number), affectedNodes: [], affectedEdges: [] };
    }
    if (operator === '>=') {
      return { matches: actual >= (value as number), affectedNodes: [], affectedEdges: [] };
    }

    return { matches: false, affectedNodes: [], affectedEdges: [] };
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function calculateMaxChainDepth(ctx: ValidationContext): number {
  let maxDepth = 0;

  for (const node of ctx.design.nodes) {
    const visited = new Set<UUID>();
    const depth = dfs(node.id, ctx.indices.adjacencyMatrix, visited);
    maxDepth = Math.max(maxDepth, depth);
  }

  return maxDepth;
}

function dfs(nodeId: UUID, adjacency: Map<UUID, Set<UUID>>, visited: Set<UUID>): number {
  if (visited.has(nodeId)) return 0;
  visited.add(nodeId);

  let maxChildDepth = 0;
  const neighbors = adjacency.get(nodeId) ?? new Set();

  for (const neighbor of neighbors) {
    const childDepth = dfs(neighbor, adjacency, new Set(visited));
    maxChildDepth = Math.max(maxChildDepth, childDepth);
  }

  return 1 + maxChildDepth;
}

function calculateCapacityRatio(ctx: ValidationContext): number {
  const estimatedQps = estimateQps(ctx);
  const requiredQps = ctx.challenge.requirements.qpsRead + ctx.challenge.requirements.qpsWrite;
  if (requiredQps === 0) return 1;
  return estimatedQps / requiredQps;
}

export function estimateQps(ctx: ValidationContext): number {
  let totalCapacity = 0;

  for (const node of ctx.design.nodes) {
    const instanceCount = node.config?.instanceCount || 1;
    totalCapacity += (node.component?.constraints?.maxTpsHeuristic || 1000) * instanceCount;
  }

  return Math.floor(totalCapacity * 0.7);
}

export function estimateLatency(ctx: ValidationContext): number {
  if (ctx.design.nodes.length === 0) return 0;

  const avgLatency = ctx.design.nodes.reduce(
    (sum, n) => sum + (n.component?.constraints?.typicalLatencyMs || 50), 0
  ) / ctx.design.nodes.length;

  const hops = ctx.design.edges.length;
  return Math.round(avgLatency + (hops * 5));
}

export function estimateCost(ctx: ValidationContext): number {
  let totalCost = 0;

  for (const node of ctx.design.nodes) {
    const instanceCount = node.config?.instanceCount || 1;
    totalCost += (node.component?.costProfile?.monthlyBaseCost || 0) * instanceCount;
  }

  return totalCost;
}

// ============================================================================
// RULE EVALUATION
// ============================================================================

function ruleApplies(rule: ValidationRule, ctx: ValidationContext): boolean {
  if (!rule.appliesWhen) return true;

  const { appliesWhen } = rule;

  // Check component presence
  if (appliesWhen.componentPresent) {
    for (const subtype of appliesWhen.componentPresent) {
      if (!ctx.indices.nodesBySubtype.has(subtype)) {
        return false;
      }
    }
  }

  // Check component absence
  if (appliesWhen.componentAbsent) {
    for (const subtype of appliesWhen.componentAbsent) {
      if (ctx.indices.nodesBySubtype.has(subtype)) {
        return false;
      }
    }
  }

  return true;
}

function interpolateMessage(
  template: string,
  ctx: ValidationContext,
  affectedNodes: UUID[],
  affectedEdges: UUID[]
): string {
  let message = template;

  // Replace node references
  if (affectedNodes.length > 0) {
    const sourceNode = ctx.indices.nodeById.get(affectedNodes[0]);
    const targetNode = affectedNodes.length > 1 ? ctx.indices.nodeById.get(affectedNodes[1]) : undefined;

    message = message.replace(/\{\{sourceNode\}\}/g, sourceNode?.label || 'Unknown');
    message = message.replace(/\{\{targetNode\}\}/g, targetNode?.label || 'Unknown');
    message = message.replace(/\{\{node\}\}/g, sourceNode?.label || 'Unknown');
  }

  // Replace edge references
  if (affectedEdges.length > 0) {
    const edge = ctx.indices.edgeById.get(affectedEdges[0]);
    if (edge) {
      const sourceNode = ctx.indices.nodeById.get(edge.sourceNodeId);
      const targetNode = ctx.indices.nodeById.get(edge.targetNodeId);
      message = message.replace(/\{\{sourceNode\}\}/g, sourceNode?.label || 'Unknown');
      message = message.replace(/\{\{targetNode\}\}/g, targetNode?.label || 'Unknown');
    }
  }

  return message;
}

function evaluateRule(rule: ValidationRule, ctx: ValidationContext): Violation[] {
  const violations: Violation[] = [];

  // Collect all condition results
  const conditionResults = rule.conditions.map(condition => {
    const evaluator = conditionEvaluators[condition.type];
    if (!evaluator) {
      console.warn(`Unknown condition type: ${condition.type}`);
      return { matches: false, affectedNodes: [], affectedEdges: [] };
    }
    const result = evaluator(condition.params, ctx);
    return condition.negate ? { ...result, matches: !result.matches } : result;
  });

  // All conditions must match for a violation
  const allConditionsMatch = conditionResults.every(r => r.matches);

  if (allConditionsMatch) {
    // Collect affected elements
    const affectedNodes = [...new Set(conditionResults.flatMap(r => r.affectedNodes))];
    const affectedEdges = [...new Set(conditionResults.flatMap(r => r.affectedEdges))];

    // Generate message
    const message = interpolateMessage(rule.messageTemplate, ctx, affectedNodes, affectedEdges);

    violations.push({
      id: crypto.randomUUID ? crypto.randomUUID() : `${rule.id}-${Date.now()}`,
      ruleId: rule.id,
      ruleName: rule.name,
      severity: rule.severity,
      message,
      affectedNodes,
      affectedEdges,
      fixes: rule.fixes,
      scorePenalty: rule.scorePenalty,
      location: affectedNodes.length > 0
        ? { nodeId: affectedNodes[0] }
        : affectedEdges.length > 0
          ? { edgeId: affectedEdges[0] }
          : undefined,
    });
  }

  return violations;
}

// ============================================================================
// SCORING
// ============================================================================

function createEmptyDimensionScore(weight: number): DimensionScore {
  return {
    score: 100,
    maxScore: 100,
    weight,
    weightedScore: 100 * weight,
    factors: [],
    suggestions: [],
  };
}

function calculateScores(
  ctx: ValidationContext,
  violations: Violation[]
): ValidationResult['scores'] {
  const weights = ctx.challenge.scoringWeights;

  const scores = {
    correctness: createEmptyDimensionScore(weights.correctness),
    scalability: createEmptyDimensionScore(weights.scalability),
    reliability: createEmptyDimensionScore(weights.reliability),
    latency: createEmptyDimensionScore(weights.latency),
    cost: createEmptyDimensionScore(weights.cost),
    security: createEmptyDimensionScore(weights.security),
    operability: createEmptyDimensionScore(weights.operability),
  };

  // Apply penalties from violations
  for (const violation of violations) {
    const dimension = violation.scorePenalty.dimension as keyof typeof scores;
    if (scores[dimension]) {
      scores[dimension].score -= violation.scorePenalty.penalty;
      scores[dimension].factors.push({
        name: violation.ruleName,
        impact: -violation.scorePenalty.penalty,
        reason: violation.message,
      });
    }
  }

  // Ensure scores don't go below 0 and recalculate weighted scores
  for (const key of Object.keys(scores) as Array<keyof typeof scores>) {
    scores[key].score = Math.max(0, Math.min(100, scores[key].score));
    scores[key].weightedScore = scores[key].score * scores[key].weight;
  }

  // Add suggestions based on low scores
  if (scores.correctness.score < 70) {
    scores.correctness.suggestions.push('Review protocol compatibility between components');
  }
  if (scores.scalability.score < 70) {
    scores.scalability.suggestions.push('Add load balancer for horizontal scaling');
  }
  if (scores.reliability.score < 70) {
    scores.reliability.suggestions.push('Add database replicas to eliminate single points of failure');
  }
  if (scores.latency.score < 70) {
    scores.latency.suggestions.push('Add caching layer (Redis) to reduce latency');
  }
  if (scores.security.score < 70) {
    scores.security.suggestions.push('Ensure all external traffic uses HTTPS');
  }
  if (scores.operability.score < 70) {
    scores.operability.suggestions.push('Add monitoring stack (Prometheus, ELK)');
  }

  return scores;
}

function calculateFinalScore(scores: ValidationResult['scores']): number {
  return Math.round(
    scores.correctness.weightedScore +
    scores.scalability.weightedScore +
    scores.reliability.weightedScore +
    scores.latency.weightedScore +
    scores.cost.weightedScore +
    scores.security.weightedScore +
    scores.operability.weightedScore
  );
}

function calculateGrade(score: number): ValidationResult['grade'] {
  if (score >= 95) return 'S';
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

// ============================================================================
// MAIN VALIDATION FUNCTIONS
// ============================================================================

export function validateDesign(
  design: DesignGraph,
  challenge: ChallengeSpec | null | undefined,
  ruleset: RuleSet = DEFAULT_RULESET
): ValidationResult {
  const startTime = performance.now();

  // Merge with default challenge spec to ensure all required fields exist
  const effectiveChallenge: ChallengeSpec = {
    ...DEFAULT_CHALLENGE_SPEC,
    ...(challenge || {}),
    requirements: {
      ...DEFAULT_CHALLENGE_SPEC.requirements,
      ...(challenge?.requirements || {}),
    },
    scoringWeights: {
      ...DEFAULT_CHALLENGE_SPEC.scoringWeights,
      ...(challenge?.scoringWeights || {}),
    },
    expectedPatterns: {
      ...DEFAULT_CHALLENGE_SPEC.expectedPatterns,
      ...(challenge?.expectedPatterns || {}),
    },
  };

  // Build indices
  const indices = buildIndices(design);

  // Create context
  const ctx: ValidationContext = {
    design,
    challenge: effectiveChallenge,
    ruleset,
    indices,
    cache: new Map(),
  };

  // Evaluate all rules
  const violations: Violation[] = [];

  for (const rule of ruleset.rules) {
    if (!rule.enabled) continue;
    if (!ruleApplies(rule, ctx)) continue;

    const ruleViolations = evaluateRule(rule, ctx);
    violations.push(...ruleViolations);
  }

  // Calculate scores
  const scores = calculateScores(ctx, violations);
  const finalScore = calculateFinalScore(scores);

  return {
    isValid: violations.filter(v => v.severity === 'error').length === 0,
    violations,
    errors: violations.filter(v => v.severity === 'error'),
    warnings: violations.filter(v => v.severity === 'warning'),
    infos: violations.filter(v => v.severity === 'info'),
    scores,
    finalScore,
    grade: calculateGrade(finalScore),
    metadata: {
      validatedAt: new Date().toISOString(),
      validationDurationMs: performance.now() - startTime,
      rulesEvaluated: ruleset.rules.filter(r => r.enabled).length,
      rulesetVersion: ruleset.version,
    },
  };
}

export function validateDesignFast(
  design: DesignGraph,
  challenge: ChallengeSpec | null | undefined
): ValidationResult {
  const fastRules = getFastRules();
  const fastRuleset: RuleSet = {
    ...DEFAULT_RULESET,
    rules: fastRules,
  };

  return validateDesign(design, challenge, fastRuleset);
}

// ============================================================================
// DESIGN GRAPH CONVERSION (from canvas format)
// ============================================================================

// Map common component name patterns to ontology IDs
const COMPONENT_NAME_PATTERNS: Array<{ pattern: RegExp; ontologyId: string }> = [
  // Cache
  { pattern: /elasticache|redis.*cluster|redis.*cache/i, ontologyId: 'elasticache' },
  { pattern: /^redis(?!\s*cluster)/i, ontologyId: 'redis' },
  { pattern: /cloudfront|cdn/i, ontologyId: 'cloudfront' },
  // Database
  { pattern: /aurora|amazon\s*aurora/i, ontologyId: 'aurora' },
  { pattern: /postgresql|postgres/i, ontologyId: 'postgresql' },
  { pattern: /mysql/i, ontologyId: 'mysql' },
  { pattern: /mongodb|mongo/i, ontologyId: 'mongodb' },
  { pattern: /dynamodb|dynamo/i, ontologyId: 'dynamodb' },
  // Queue
  { pattern: /kafka|msk/i, ontologyId: 'kafka' },
  { pattern: /sqs|simple\s*queue/i, ontologyId: 'sqs' },
  { pattern: /rabbitmq|rabbit/i, ontologyId: 'rabbitmq' },
  // Compute
  { pattern: /lambda/i, ontologyId: 'lambda' },
  { pattern: /ecs|fargate/i, ontologyId: 'ecs' },
  { pattern: /eks|kubernetes|k8s/i, ontologyId: 'ecs' }, // Map EKS to ECS (similar container compute)
  { pattern: /ec2/i, ontologyId: 'ec2' },
  { pattern: /gke/i, ontologyId: 'gke' },
  { pattern: /cloud\s*run/i, ontologyId: 'cloud-run' },
  // Gateway & Load Balancer
  { pattern: /api\s*gateway/i, ontologyId: 'api-gateway' },
  { pattern: /alb|application\s*load\s*balancer/i, ontologyId: 'alb' },
  { pattern: /nlb|network\s*load\s*balancer/i, ontologyId: 'nlb' },
  // Storage
  { pattern: /s3|simple\s*storage/i, ontologyId: 's3' },
  // Security
  { pattern: /waf|web\s*application\s*firewall/i, ontologyId: 'waf' },
  { pattern: /cognito/i, ontologyId: 'cognito' },
  // Monitoring
  { pattern: /prometheus/i, ontologyId: 'prometheus' },
  { pattern: /elasticsearch|opensearch|elastic/i, ontologyId: 'elasticsearch' },
  // DNS
  { pattern: /route\s*53/i, ontologyId: 'route53' },
  // Client
  { pattern: /web\s*browser|web\s*client/i, ontologyId: 'web-client' },
  { pattern: /mobile\s*app|mobile\s*client/i, ontologyId: 'mobile-client' },
];

// Find ontology component from node data using fuzzy matching
function findOntologyComponent(nodeData: any): ComponentDefinition {
  // 1. Try exact ontology ID match (if componentId is a string ID)
  if (typeof nodeData?.componentId === 'string') {
    const exact = getComponentById(nodeData.componentId);
    if (exact) return exact;
  }

  // 2. Try to match by label using patterns
  const label = nodeData?.label || '';
  for (const { pattern, ontologyId } of COMPONENT_NAME_PATTERNS) {
    if (pattern.test(label)) {
      const matched = getComponentById(ontologyId);
      if (matched) return matched;
    }
  }

  // 3. Try exact name match in ontology
  const byName = COMPONENT_ONTOLOGY.find(c => c.name === label);
  if (byName) return byName;

  // 4. Try matching by category and partial name
  const category = nodeData?.category;
  if (category) {
    const categoryComponents = COMPONENT_ONTOLOGY.filter(c => c.category === category);
    if (categoryComponents.length > 0) {
      // Try to find best match by name similarity
      for (const comp of categoryComponents) {
        if (label.toLowerCase().includes(comp.name.toLowerCase()) ||
            comp.name.toLowerCase().includes(label.toLowerCase().split(' ')[0])) {
          return comp;
        }
      }
      // Default to first component in category
      return categoryComponents[0];
    }
  }

  // 5. Fallback to first component
  return COMPONENT_ONTOLOGY[0];
}

export function convertCanvasToDesignGraph(
  canvasNodes: any[],
  canvasEdges: any[],
  challengeId: string,
  userId: string = 'anonymous'
): DesignGraph {
  const nodes: DesignNode[] = canvasNodes.map(node => {
    const componentDef = findOntologyComponent(node.data);

    return {
      id: node.id,
      componentId: componentDef.id, // Use ontology ID for consistency
      component: componentDef,
      label: node.data?.label || componentDef.name,
      position: node.position || { x: 0, y: 0 },
      config: {
        instanceCount: node.data?.instanceCount || 1,
        regions: node.data?.regions || [],
        isReplica: node.data?.isReplica || false,
        replicaOf: node.data?.replicaOf,
        properties: {},
      },
    };
  });

  const edges: DesignEdge[] = canvasEdges.map(edge => ({
    id: edge.id,
    sourceNodeId: edge.source,
    sourcePortId: edge.sourceHandle || 'default',
    targetNodeId: edge.target,
    targetPortId: edge.targetHandle || 'default',
    protocol: (edge.data?.protocol || 'https') as Protocol,
    semantic: (edge.data?.semantic || 'request_response') as any,
    purposes: edge.data?.purposes || ['read_path'],
    encrypted: edge.data?.encrypted ?? true,
    metadata: {
      notes: edge.data?.notes,
      expectedRps: edge.data?.expectedRps,
      criticalPath: edge.data?.criticalPath || false,
    },
  }));

  return {
    id: crypto.randomUUID ? crypto.randomUUID() : `design-${Date.now()}`,
    challengeId,
    userId,
    nodes,
    edges,
    metadata: {
      name: 'Design',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    },
  };
}
